import { useDraggable } from '@dnd-kit/core';
import ScrollArea from '../ui/scroll-area';
import type { BasicBlockType, SmartBlockType, SmartButtonType } from '../../email-builder/emailBuilder.store';
import { useEmailBuilderStore } from '../../email-builder/emailBuilder.store';
import { SMART_BUTTON_META, BUTTON_ICON_SVG } from './blocks/shared';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useState } from 'react';

// ─── Skeleton helpers ─────────────────────────────────────────────────────────

const SkeletonLine = ({ w = 'w-full', h = 'h-2' }: { w?: string; h?: string }) => (
  <div className={cn('rounded-sm bg-elva-surface-mute', w, h)} />
);

const SkeletonImage = ({ className }: { className?: string }) => (
  <div className={cn('rounded bg-elva-surface-mute flex items-center justify-center', className)}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-elva-ink-4">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  </div>
);

// ─── Block skeleton previews ──────────────────────────────────────────────────

const SkeletonEmailHeader = () => (
  <div className="flex items-center gap-2 px-2 py-2">
    <div className="h-5 w-5 rounded-full bg-elva-surface-mute shrink-0" />
    <div className="flex-1">
      <SkeletonLine w="w-3/4" h="h-1.5" />
    </div>
  </div>
);

const SkeletonEmailFooter = () => (
  <div className="px-2 py-2 space-y-1.5">
    <SkeletonLine w="w-full" h="h-1.5" />
    <SkeletonLine w="w-full" h="h-1.5" />
    <SkeletonLine w="w-2/3" h="h-1.5" />
  </div>
);

const SkeletonHeaderText = () => (
  <div className="px-2 py-2 space-y-1.5">
    <SkeletonLine w="w-full" h="h-1.5" />
    <SkeletonLine w="w-3/4" h="h-1.5" />
  </div>
);

const SkeletonFullWidthText = () => (
  <div className="px-2 py-2 space-y-1">
    <SkeletonLine w="w-full" h="h-1.5" />
    <SkeletonLine w="w-full" h="h-1.5" />
    <SkeletonLine w="w-full" h="h-1.5" />
    <SkeletonLine w="w-2/3" h="h-1.5" />
  </div>
);

const SkeletonTwoColumnText = () => (
  <div className="px-2 py-2 flex gap-3">
    <div className="flex-1 space-y-1">
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-3/4" h="h-1.5" />
    </div>
    <div className="flex-1 space-y-1">
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-3/4" h="h-1.5" />
    </div>
  </div>
);

const SkeletonTextImage = () => (
  <div className="px-2 py-2 flex gap-2 items-center">
    <SkeletonImage className="w-1/2 h-12 shrink-0" />
    <div className="flex-1 space-y-1">
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-3/4" h="h-1.5" />
    </div>
  </div>
);

const SkeletonImageText = () => (
  <div className="px-2 py-2 flex gap-2 items-center">
    <div className="flex-1 space-y-1">
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-full" h="h-1.5" />
      <SkeletonLine w="w-3/4" h="h-1.5" />
    </div>
    <SkeletonImage className="w-1/2 h-12 shrink-0" />
  </div>
);

const SkeletonFullWidthImage = () => (
  <div className="px-2 py-2">
    <SkeletonImage className="w-full h-12" />
  </div>
);

const SkeletonSeparator = () => (
  <div className="px-4 py-3 flex items-center justify-center">
    <div className="w-full h-px rounded-full bg-elva-surface-inset" />
  </div>
);

const SkeletonTwoColumnImages = () => (
  <div className="px-2 py-2 flex gap-2">
    <SkeletonImage className="flex-1 h-12" />
    <SkeletonImage className="flex-1 h-12" />
  </div>
);

// ─── Block icon colors ────────────────────────────────────────────────────────

interface BlockIconConfig {
  bg: string;
  icon: React.ReactNode;
}

const EmailHeaderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const EmailFooterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="15" y2="18" />
  </svg>
);

const HeaderTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
  </svg>
);

const FullWidthTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="3" y1="14" x2="21" y2="14" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const TwoColumnTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="10" y2="6" />
    <line x1="3" y1="10" x2="10" y2="10" />
    <line x1="3" y1="14" x2="10" y2="14" />
    <line x1="14" y1="6" x2="21" y2="6" />
    <line x1="14" y1="10" x2="21" y2="10" />
    <line x1="14" y1="14" x2="21" y2="14" />
  </svg>
);

const TextImageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="13" y="4" width="8" height="8" rx="1" />
    <line x1="3" y1="6" x2="10" y2="6" />
    <line x1="3" y1="10" x2="10" y2="10" />
    <line x1="3" y1="14" x2="21" y2="14" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const ImageTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="8" height="8" rx="1" />
    <line x1="14" y1="6" x2="21" y2="6" />
    <line x1="14" y1="10" x2="21" y2="10" />
    <line x1="3" y1="14" x2="21" y2="14" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const TwoImageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="9" height="9" rx="1" />
    <rect x="13" y="4" width="9" height="9" rx="1" />
    <line x1="2" y1="17" x2="22" y2="17" />
    <line x1="2" y1="20" x2="16" y2="20" />
  </svg>
);

const FullImageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <circle cx="8" cy="8" r="1.5" />
    <polyline points="21 15 16 10 5 17" />
  </svg>
);

const SeparatorIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="7" x2="3" y2="7" strokeLinecap="round" />
    <line x1="3" y1="17" x2="3" y2="17" strokeLinecap="round" />
  </svg>
);

// ─── Block palette definitions ────────────────────────────────────────────────

interface BlockDef {
  type: BasicBlockType;
  label: string;
  skeleton: React.ReactNode;
  iconConfig: BlockIconConfig;
}

const BASIC_BLOCKS: BlockDef[] = [
  {
    type: 'headerText',
    label: 'Header Text',
    skeleton: <SkeletonHeaderText />,
    iconConfig: { bg: 'bg-elva-channel-email-tint', icon: <HeaderTextIcon /> },
  },
  {
    type: 'fullWidthText',
    label: 'Full Width Text',
    skeleton: <SkeletonFullWidthText />,
    iconConfig: { bg: 'bg-elva-brass-tint', icon: <FullWidthTextIcon /> },
  },
  {
    type: 'twoColumnText',
    label: 'Two Column Text',
    skeleton: <SkeletonTwoColumnText />,
    iconConfig: { bg: 'bg-elva-brass-tint', icon: <TwoColumnTextIcon /> },
  },
  {
    type: 'textImage',
    label: 'Text & Image',
    skeleton: <SkeletonTextImage />,
    iconConfig: { bg: 'bg-elva-tint-lavender', icon: <TextImageIcon /> },
  },
  {
    type: 'imageText',
    label: 'Text & Image',
    skeleton: <SkeletonImageText />,
    iconConfig: { bg: 'bg-elva-tint-lavender', icon: <ImageTextIcon /> },
  },
  {
    type: 'twoColumnImages',
    label: 'Two Image',
    skeleton: <SkeletonTwoColumnImages />,
    iconConfig: { bg: 'bg-elva-tint-pink', icon: <TwoImageIcon /> },
  },
  {
    type: 'fullWidthImage',
    label: 'Full Image',
    skeleton: <SkeletonFullWidthImage />,
    iconConfig: { bg: 'bg-elva-tint-pink', icon: <FullImageIcon /> },
  },
  {
    type: 'separator',
    label: 'Separator',
    skeleton: <SkeletonSeparator />,
    iconConfig: { bg: 'bg-elva-surface-mute', icon: <SeparatorIcon /> },
  },
];

// ─── Smart block definitions ──────────────────────────────────────────────────

interface SmartBlockDef {
  type: SmartBlockType;
  label: string;
  iconConfig: BlockIconConfig;
}

const PromoTicketIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);

const FinancingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const InsuranceIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const BeforeAfterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="17 1 21 5 17 9"/>
    <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <polyline points="7 23 3 19 7 15"/>
    <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

const GoogleReviewIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const ServiceSpotlightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
  </svg>
);

const VideoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10"/>
    <polygon points="10 8 16 12 10 16 10 8"/>
  </svg>
);

const LocationIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const SMART_BLOCKS: SmartBlockDef[] = [
  {
    type: 'testimonial',
    label: 'Promo Ticket',
    iconConfig: { bg: 'bg-elva-positive-soft', icon: <PromoTicketIcon /> },
  },
  {
    type: 'financingOptions',
    label: 'Financing Options',
    iconConfig: { bg: 'bg-elva-channel-email-tint', icon: <FinancingIcon /> },
  },
  {
    type: 'experienceInsurance',
    label: 'Expiring Insurance',
    iconConfig: { bg: 'bg-elva-tint-pink', icon: <InsuranceIcon /> },
  },
  {
    type: 'beforeAfterSlider',
    label: 'Before & After Slider',
    iconConfig: { bg: 'bg-elva-channel-email-tint', icon: <BeforeAfterIcon /> },
  },
  {
    type: 'serviceSpotlight',
    label: 'Google Review Request',
    iconConfig: { bg: 'bg-elva-brass-tint', icon: <GoogleReviewIcon /> },
  },
  {
    type: 'serviceSpotlight',
    label: 'Service Spotlight',
    iconConfig: { bg: 'bg-elva-positive-soft', icon: <ServiceSpotlightIcon /> },
  },
  {
    type: 'video',
    label: 'Video Testimonial',
    iconConfig: { bg: 'bg-elva-channel-email-tint', icon: <VideoIcon /> },
  },
  {
    type: 'video',
    label: 'Location & Hours',
    iconConfig: { bg: 'bg-elva-channel-email-tint', icon: <LocationIcon /> },
  },
];

// ─── Button block definitions ─────────────────────────────────────────────────

interface ButtonBlockDef {
  type: BasicBlockType | SmartBlockType;
  label: string;
  icon?: string;
  isSmart?: boolean;
  isGoogle?: boolean;
}

const SMART_BUTTON_DEFS: ButtonBlockDef[] = (Object.entries(SMART_BUTTON_META) as [SmartButtonType, typeof SMART_BUTTON_META[SmartButtonType]][]).map(
  ([type, meta]) => ({
    type,
    label: meta.label,
    icon: meta.icon,
    isSmart: true,
    isGoogle: type === 'smartBtn_leaveReview',
  }),
);

// ─── Tab component ────────────────────────────────────────────────────────────

type TabValue = 'basic' | 'smart' | 'buttons';

// ─── Draggable palette item (Basic blocks with skeleton preview) ──────────────

interface BlockPaletteItemProps {
  def: BlockDef;
}

const BlockPaletteItem = ({ def }: BlockPaletteItemProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette::${def.type}::${def.label}`,
    data: { from: 'palette', blockType: def.type },
  });
  const addBlock = useEmailBuilderStore((s) => s.addBlock);

  return (
    <div
      ref={setNodeRef}
      aria-label={`Add ${def.label} block`}
      onDoubleClick={() => addBlock(def.type)}
      onKeyDown={(e) => e.key === 'Enter' && addBlock(def.type)}
      className={cn(
        'cursor-grab rounded-xl border border-elva-border-soft bg-elva-surface text-left transition-all duration-150 select-none overflow-hidden',
        'hover:border-elva-border-soft hover:shadow-elva-sm',
        'active:cursor-grabbing active:scale-[0.98]',
        isDragging && 'opacity-40 shadow-elva-md',
      )}
      data-id={`palette-block-${def.type}`}
      {...attributes}
      {...listeners}
    >
      {/* Label row with colored icon */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className={cn('w-6 h-6 rounded-md flex items-center justify-center shrink-0 text-elva-ink-3', def.iconConfig.bg)}>
          {def.iconConfig.icon}
        </div>
        <p className="text-sm font-medium text-elva-ink-2">{def.label}</p>
      </div>
      {/* Skeleton preview */}
      <div className="bg-elva-surface-mute border-t border-elva-border-soft min-h-[48px]">
        {def.skeleton}
      </div>
    </div>
  );
};

// ─── Structural block palette items (header / footer) ────────────────────────

const StructuralPaletteItem = ({
  structuralType,
  label,
  iconConfig,
}: {
  structuralType: 'emailHeader' | 'emailFooter';
  label: string;
  iconConfig: BlockIconConfig;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette::${structuralType}`,
    data: { from: 'palette', structuralType },
  });
  const addEmailHeader = useEmailBuilderStore((s) => s.addEmailHeader);
  const addEmailFooter = useEmailBuilderStore((s) => s.addEmailFooter);

  const handleAdd = () => {
    if (structuralType === 'emailHeader') addEmailHeader();
    else addEmailFooter();
  };

  return (
    <div
      ref={setNodeRef}
      aria-label={`Add ${label}`}
      onDoubleClick={handleAdd}
      onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
      className={cn(
        'cursor-grab rounded-xl border border-elva-border-soft bg-elva-surface text-left transition-all duration-150 select-none overflow-hidden',
        'hover:border-elva-border-soft hover:shadow-elva-sm',
        'active:cursor-grabbing active:scale-[0.98]',
        isDragging && 'opacity-40 shadow-elva-md',
      )}
      data-id={`palette-structural-${structuralType}`}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className={cn('w-6 h-6 rounded-md flex items-center justify-center shrink-0 text-elva-ink-3', iconConfig.bg)}>
          {iconConfig.icon}
        </div>
        <p className="text-sm font-medium text-elva-ink-2">{label}</p>
      </div>
      <div className="bg-elva-surface-mute border-t border-elva-border-soft min-h-[44px]">
        {structuralType === 'emailHeader' ? <SkeletonEmailHeader /> : <SkeletonEmailFooter />}
      </div>
    </div>
  );
};

// ─── Smart block palette item ─────────────────────────────────────────────────

interface SmartBlockPaletteItemProps {
  def: SmartBlockDef;
}

const SmartBlockPaletteItem = ({ def }: SmartBlockPaletteItemProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette::smart::${def.label}`,
    data: { from: 'palette', blockType: def.type },
  });
  const addBlock = useEmailBuilderStore((s) => s.addBlock);

  return (
    <div
      ref={setNodeRef}
      aria-label={`Add ${def.label} block`}
      onDoubleClick={() => addBlock(def.type)}
      onKeyDown={(e) => e.key === 'Enter' && addBlock(def.type)}
      className={cn(
        'flex cursor-grab items-center gap-3 rounded-xl border border-elva-border-soft bg-elva-surface',
        'px-3 py-3 text-left transition-all duration-150 select-none',
        'hover:border-elva-border-soft hover:shadow-elva-sm',
        'active:cursor-grabbing active:scale-[0.98]',
        isDragging && 'opacity-40 shadow-elva-md',
      )}
      data-id={`palette-smart-block-${def.label}`}
      {...attributes}
      {...listeners}
    >
      <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-elva-ink-3', def.iconConfig.bg)}>
        {def.iconConfig.icon}
      </div>
      <p className="text-sm font-medium text-elva-ink-2">{def.label}</p>
    </div>
  );
};

// ─── Button block palette item ────────────────────────────────────────────────

interface ButtonBlockPaletteItemProps {
  def: ButtonBlockDef;
}

const GoogleGIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const ButtonBlockPaletteItem = ({ def }: ButtonBlockPaletteItemProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette::${def.type}`,
    data: { from: 'palette', blockType: def.type },
  });
  const addBlock = useEmailBuilderStore((s) => s.addBlock);
  const iconSvg = def.icon ? BUTTON_ICON_SVG[def.icon] : undefined;

  return (
    <div
      ref={setNodeRef}
      aria-label={`Add ${def.label} button`}
      onDoubleClick={() => addBlock(def.type as BasicBlockType)}
      onKeyDown={(e) => e.key === 'Enter' && addBlock(def.type as BasicBlockType)}
      className={cn(
        'cursor-grab rounded-lg  bg-elva-surface text-left transition-all duration-150 select-none overflow-hidden',
        'hover:border-elva-border-soft hover:shadow-elva-sm active:cursor-grabbing active:scale-[0.98]',
        isDragging && 'opacity-40 shadow-elva-md',
      )}
      data-id={`palette-button-block-${def.type}`}
      {...attributes}
      {...listeners}
    >
      <div className=" flex items-center justify-center">
        <div className="flex items-center gap-2 rounded-lg border border-elva-border-soft px-4 py-2 text-sm text-elva-ink-2 w-full justify-center bg-elva-surface">
          {def.isGoogle ? (
            <GoogleGIcon />
          ) : iconSvg ? (
            <span
              className="shrink-0 text-elva-ink-3"
              style={{ width: 14, height: 14, display: 'inline-flex', alignItems: 'center' }}
              dangerouslySetInnerHTML={{ __html: iconSvg }}
            />
          ) : null}
          <span className="truncate">{def.label}</span>
          {def.isGoogle && (
            <span className="text-xs ml-0.5">⭐⭐⭐⭐⭐</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export const BlocksSidebar = () => {
  const emailHeader = useEmailBuilderStore((s) => s.emailHeader);
  const emailFooter = useEmailBuilderStore((s) => s.emailFooter);
  const [activeTab, setActiveTab] = useState<TabValue>('basic');

  return (
    <aside
      className="flex h-full flex-col overflow-hidden rounded-xl border border-elva-border-soft bg-elva-surface shadow-elva-sm"
      data-id="email-builder-sidebar"
    >
      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-elva-border-soft bg-elva-surface-mute p-2">
        <button
          data-id="tab-basic"
          onClick={() => setActiveTab('basic')}
          className={cn(
            'flex-1 rounded-xl py-2.5 text-sm font-medium transition-all',
            activeTab === 'basic'
              ? 'bg-elva-green-deep text-elva-surface shadow-elva-sm'
              : 'text-elva-ink-4 hover:text-elva-ink-3',
          )}
        >
          Basics
        </button>
        <button
          data-id="tab-smart"
          onClick={() => setActiveTab('smart')}
          className={cn(
            'flex-1 rounded-xl py-2 text-sm font-medium transition-all flex flex-col items-center gap-0',
            activeTab === 'smart'
              ? 'bg-elva-green-deep text-elva-surface shadow-elva-sm'
              : 'text-elva-ink-4 hover:text-elva-ink-3',
          )}
        >
          <span className="flex items-center gap-0.5 text-[10px] font-normal opacity-80">
            <Sparkles size={8} />
            Smart
          </span>
          <span className="text-sm font-semibold leading-tight">Blocks</span>
        </button>
        <button
          data-id="tab-buttons"
          onClick={() => setActiveTab('buttons')}
          className={cn(
            'flex-1 rounded-xl py-2 text-sm font-medium transition-all flex flex-col items-center gap-0',
            activeTab === 'buttons'
              ? 'bg-elva-green-deep text-elva-surface shadow-elva-sm'
              : 'text-elva-ink-4 hover:text-elva-ink-3',
          )}
        >
          <span className="flex items-center gap-0.5 text-[10px] font-normal opacity-80">
            <Sparkles size={8} />
            Smart
          </span>
          <span className="text-sm font-semibold leading-tight">Buttons</span>
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'basic' && (
          <ScrollArea className="h-full">
            <div className="flex flex-col gap-2 p-3">
              {!emailHeader && (
                <StructuralPaletteItem
                  structuralType="emailHeader"
                  label="Email Header"
                  iconConfig={{ bg: 'bg-elva-positive-soft', icon: <EmailHeaderIcon /> }}
                />
              )}
              {!emailFooter && (
                <StructuralPaletteItem
                  structuralType="emailFooter"
                  label="Email Footer"
                  iconConfig={{ bg: 'bg-elva-positive-soft', icon: <EmailFooterIcon /> }}
                />
              )}
              {BASIC_BLOCKS.map((def) => (
                <BlockPaletteItem key={`${def.type}-${def.label}`} def={def} />
              ))}
            </div>
          </ScrollArea>
        )}

        {activeTab === 'smart' && (
          <ScrollArea className="h-full">
            <div className="flex flex-col gap-2 p-3">
              {SMART_BLOCKS.map((def) => (
                <SmartBlockPaletteItem key={def.label} def={def} />
              ))}
            </div>
          </ScrollArea>
        )}

        {activeTab === 'buttons' && (
          <ScrollArea className="h-full">
            <div className="flex flex-col gap-3 p-3">
              {(['Financial & Treatment', 'Scheduling & Triage', 'Offers & Claims', 'Administrative & Loyalty'] as const).map((group) => {
                const groupDefs = SMART_BUTTON_DEFS.filter((d) => SMART_BUTTON_META[d.type as SmartButtonType].group === group);
                return (
                  <div key={group} className="rounded-xl border border-elva-border-soft bg-elva-surface overflow-hidden">
                    <div className="px-3 py-2.5 border-b border-elva-border-soft">
                      <p className="text-sm font-semibold text-elva-ink-2">{group}</p>
                    </div>
                    <div className="p-2 flex flex-col gap-3">
                      {groupDefs.map((def) => (
                        <ButtonBlockPaletteItem key={def.type} def={def} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>
    </aside>
  );
};
